/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Form, Input, Message, Modal, Popconfirm, Select, Table, TableColumnProps, Tag } from '@arco-design/web-react';
import dayjs from 'dayjs';
import { post, get } from '../requestNew';

import './index.scss';
import { DEFAULT_RISK_LEVEL, riskLevelOption, riskLevelTagMap } from '../utils';

const FormItem = Form.Item;
const Option = Select.Option;

interface CommentItem {
  id: number;
  gameSession: string;
  gameName: string;
  content: string;
  likeNum: number;
  replayNum: number;
  createTime: string;
  commentDate: string;
  riskConfidence?: number | string;
  riskLevel?: string;
  riskDescription?: string;
}

interface ReplyItem {
  id: number;
  gameName: string;
  replyToGameName?: string;
  content: string;
  createTime: string;
}

function RankTopComment() {
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<CommentItem[]>([]);
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyList, setReplyList] = useState<ReplyItem[]>([]);
  const [currentCommentId, setCurrentCommentId] = useState<number | null>(null);
  const [gameSessionList, setGameSessionList] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  /** 工作台跳转带过来的赛季、日期与风险等级，每次进入页面时读取 */
  const initialQuery = useMemo(() => new URLSearchParams(window.location.search), []);
  const [riskLevel, setRiskLevel] = useState(initialQuery.get('riskLevel') || DEFAULT_RISK_LEVEL);
  const hasInitialized = useRef(false);
  const isRiskQuery = riskLevel !== DEFAULT_RISK_LEVEL;

  const riskColumns: TableColumnProps[] = [
    {
      title: '风险评分',
      dataIndex: 'riskConfidence',
      width: 100,
      render: text => {
        if (text === null || text === undefined || text === '') {
          return '-';
        }
        const num = Number(text);
        return Number.isNaN(num) ? text : num.toFixed(2);
      }
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      width: 100,
      render: text => {
        const info = riskLevelTagMap[text];
        if (!info) {
          return '-';
        }
        return (
          <Tag color={info.bg} style={{ color: info.text, border: 'none' }}>
            {info.label}
          </Tag>
        );
      }
    },
    {
      title: '风险描述',
      dataIndex: 'riskDescription',
      width: 200,
      render: text => text || '-'
    }
  ];

  // 获取赛季列表并设置默认值
  const getGameSessionList = () => {
    get('/gameConfig/getGameSessionList')
      .then((res: any) => {
        const { data } = res || {};
        if (data && Array.isArray(data) && data.length > 0) {
          const options = data.map((item: any) => ({
            label: item,
            value: item
          }));
          
          // 对 options 进行排序：数字类型排前面（按数字降序），字母类型排后面（按首字母字典排序）
          const sortedOptions = options.sort((a, b) => {
            const aValue = String(a.value);
            const bValue = String(b.value);
            const aIsNumeric = /^\d+$/.test(aValue);
            const bIsNumeric = /^\d+$/.test(bValue);
            
            // 如果都是数字类型，按数字降序排列
            if (aIsNumeric && bIsNumeric) {
              return parseInt(bValue, 10) - parseInt(aValue, 10);
            }
            // 如果只有 a 是数字，a 排前面
            if (aIsNumeric && !bIsNumeric) {
              return -1;
            }
            // 如果只有 b 是数字，b 排前面
            if (!aIsNumeric && bIsNumeric) {
              return 1;
            }
            // 如果都不是数字，按首字母字典排序
            return aValue.localeCompare(bValue);
          });
          
          setGameSessionList(sortedOptions);
          // 默认选中第一个（最新赛季）；URL 指定了赛季时优先用 URL
          const urlSession = initialQuery.get('gameSession');
          const defaultSession =
            urlSession && sortedOptions.some(option => String(option.value) === urlSession)
              ? urlSession
              : sortedOptions[0].value;
          form.setFieldValue('gameSession', defaultSession);
          // 默认选中今天；URL 指定了日期时优先用 URL
          const urlDate = initialQuery.get('date');
          form.setFieldValue('commentDate', urlDate ? dayjs(urlDate) : dayjs());
        }
      })
      .catch(() => {
        Message.error('获取赛季列表失败!');
      });
  };

  // 获取评论列表
  const getList = (reset: boolean, overrides: Record<string, any> = {}) => {
    if (reset) {
      setPageNum(1);
    }
    setLoading(true);
    const obj: any = form.getFieldsValue();
    
    // 确保必填参数存在
    if (!obj.gameSession || !obj.commentDate) {
      Message.warning('请选择赛季和日期');
      setLoading(false);
      return;
    }

    const params: any = {
      gameSession: obj.gameSession,
      commentDate: dayjs(obj.commentDate).format('YYYY-MM-DD'),
      pageSize: reset ? 20 : pageSize,
      pageNum: reset ? 1 : pageNum,
      ...overrides
    };
    const finalRiskLevel = params.riskLevel !== undefined ? params.riskLevel : riskLevel;
    if (finalRiskLevel && finalRiskLevel !== DEFAULT_RISK_LEVEL) {
      params.riskLevel = finalRiskLevel;
    } else {
      delete params.riskLevel;
    }

    // 添加可选参数
    if (obj.content) {
      params.content = obj.content;
    }
    if (obj.gameName) {
      params.gameName = obj.gameName;
    }

    get('/commentReply/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total } = data || {};
          setTotal(total || 0);
          setDataList(list || []);
        } else {
          Message.error(message || '请求失败');
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 重置筛选条件
  const handleReset = () => {
    form.resetFields();
    setRiskLevel(DEFAULT_RISK_LEVEL);
    setPageNum(1);
    setPageSize(20);
    // 重新设置默认值
    if (gameSessionList.length > 0) {
      form.setFieldValue('gameSession', gameSessionList[0].value);
      form.setFieldValue('commentDate', dayjs());
      getList(true, { riskLevel: DEFAULT_RISK_LEVEL });
    }
  };

  // 显示回复列表
  const showReply = (record: CommentItem) => {
    setCurrentCommentId(record.id);
    setReplyVisible(true);
    getReplyList(record.id, 1, 100);
  };

  // 获取回复列表
  const getReplyList = (parentId: number, pageNum: number = 1, pageSize: number = 100) => {
    get('/commentReply/replyList', { parentId, pageNum, pageSize })
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list } = data || {};
          setReplyList(list || []);
        } else {
          Message.error(message || '获取回复列表失败');
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      });
  };

  // 关闭回复列表
  const closeReply = () => {
    setReplyVisible(false);
    setReplyList([]);
    setCurrentCommentId(null);
  };

  // 删除回复
  const delReply = (record: ReplyItem) => {
    post(`/commentReply/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          if (currentCommentId) {
            getReplyList(currentCommentId, 1, 100);
          }
        } else {
          Message.error(message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  // 删除评论
  const del = (record: CommentItem) => {
    post(`/commentReply/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          if (dataList.length > 1) {
            getList(false);
          } else {
            setPageNum(Math.max(1, pageNum - 1));
            getList(false);
          }
        } else {
          Message.error(message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const confirmRiskCommentReply = (record: CommentItem) => {
    if (!record.id) {
      Message.error('评论ID不存在！');
      return;
    }
    post(`/commentReply/confirmRiskCommentReply?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('操作成功！');
          getList(false);
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  // 表格列配置
  const columns: TableColumnProps[] = [
    {
      title: '评论选手',
      dataIndex: 'gameName',
      width: 120
    },
    {
      title: '评论内容',
      dataIndex: 'content',
      width: 400
    },
    {
      title: '点赞数',
      dataIndex: 'likeNum',
      width: 100,
      render: (text) => text || 0
    },
    {
      title: '回复数',
      dataIndex: 'replayNum',
      width: 100,
      render: (text) => text || 0
    },
    {
      title: '评论时间',
      dataIndex: 'createTime',
      width: 180,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    ...(isRiskQuery ? riskColumns : []),
    {
      title: '操作',
      dataIndex: 'action',
      width: 150,
      render: (_, record: CommentItem) => (
        <>
          {isRiskQuery && (
            <Popconfirm
              focusLock
              title="提示"
              content="是否确认解除该评论风险？"
              onOk={() => confirmRiskCommentReply(record)}
            >
              <Button className="btn" type="text">
                风险解除
              </Button>
            </Popconfirm>
          )}
          <Button className="btn" type="text" onClick={() => showReply(record)} style={{ marginRight: '8px' }}>
            回复管理
          </Button>
          <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => del(record)}>
            <Button className="btn" type="text" status="danger">
              删除
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  // 回复列表表格列配置
  const replyColumns: TableColumnProps[] = [
    {
      title: '回复人',
      dataIndex: 'gameName',
      width: 120
    },
    {
      title: '被回复',
      dataIndex: 'replyToGameName',
      width: 120,
      render: (text) => text ? `@${text}` : '--'
    },
    {
      title: '内容',
      dataIndex: 'content',
      width: 300
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      width: 180,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      dataIndex: 'replyAction',
      width: 80,
      render: (_, record: ReplyItem) => (
        <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => delReply(record)}>
          <Button className="btn" type="text" status="danger">
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  const handleRiskLevelChange = (val: string) => {
    const next = val ?? DEFAULT_RISK_LEVEL;
    setRiskLevel(next);
    if (pageNum === 1) {
      getList(false, { riskLevel: next });
    } else {
      setPageNum(1);
    }
  };

  useEffect(() => {
    getGameSessionList();
  }, []);

  useEffect(() => {
    // 当赛季列表加载完成后，如果有默认值，自动查询一次
    if (gameSessionList.length > 0 && form.getFieldValue('gameSession') && form.getFieldValue('commentDate') && !hasInitialized.current) {
      hasInitialized.current = true;
      getList(false);
    }
  }, [gameSessionList]);

  useEffect(() => {
    // 当分页参数变化时，重新查询
    if (gameSessionList.length > 0 && form.getFieldValue('gameSession') && form.getFieldValue('commentDate')) {
      getList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize]);

  return (
    <div className="rank-top-comment table-flex">
      <Form style={{ width: '100%' }} autoComplete="off" layout="inline" form={form}>
        <FormItem label="赛季" field="gameSession" rules={[{ required: true, message: '请选择赛季' }]}>
          <Select style={{ width: 150 }} placeholder="请选择赛季">
            {gameSessionList.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </FormItem>
        <FormItem label="评论选手" field="gameName">
          <Input style={{ width: 120 }} placeholder="请输入评论选手" />
        </FormItem>
        <FormItem label="评论内容" field="content">
          <Input style={{ width: 200 }} placeholder="请输入评论内容" />
        </FormItem>
        <FormItem label="评论日期" field="commentDate" rules={[{ required: true, message: '请选择日期' }]}>
          <DatePicker
            style={{ width: 200 }}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </FormItem>
        <FormItem label="风险等级">
          <Select
            value={riskLevel}
            onChange={handleRiskLevelChange}
            style={{ width: 120 }}
          >
            {riskLevelOption.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem>
          <Button type="primary" onClick={() => getList(true)} loading={loading}>
            查询
          </Button>
        </FormItem>
        <FormItem>
          <Button type="outline" onClick={handleReset} loading={loading}>
            重置
          </Button>
        </FormItem>
      </Form>
      <div className="table-content">
        <Table
          rowKey="id"
          columns={columns}
          data={dataList}
          className="table-scroll"
          loading={loading}
          pagination={{
            sizeCanChange: true,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            sizeOptions: [20, 30, 50, 100],
            onChange: (num, size) => {
              setPageNum(num);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
      <Modal
        title="回复管理"
        visible={replyVisible}
        style={{ width: 1000 }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={closeReply}
        onOk={closeReply}
        unmountOnExit
      >
        <Table
          rowKey="id"
          columns={replyColumns}
          data={replyList}
          pagination={false}
          scroll={{ y: 600 }}
        />
      </Modal>
    </div>
  );
}

export default RankTopComment;
